import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useGuestSession } from "contexts/GuestSessionContext";
import {
  ROOM_FOR_YOU_IDS,
  ROOM_QUICK_IDS,
  TABLE_FOR_YOU_IDS,
  TABLE_QUICK_IDS,
  withGuestSearch,
} from "data/guestPortalDefaults";
import { LUXURY } from "data/guestLuxuryTheme";
import HubSearch from "./components/HubSearch";
import ServiceTile from "./components/ServiceTile";
import QuickAction from "./components/QuickAction";
import FeaturedFoodCard from "./components/FeaturedFoodCard";
import EventsCarousel from "./components/EventsCarousel";
import SectionHeader from "./components/SectionHeader";
import ConciergeBanner from "./components/ConciergeBanner";
import RatingDialog from "pages/RestaurantMenu/components/RatingDialog";
import { useGuestHubSearch } from "./GuestHubSearchContext";

const QUICK_META = {
  housekeeping: { title: "Housekeeping", action: "Request", illustration: "housekeeping" },
  makeup: { title: "Make up room", action: "Request", illustration: "makeup" },
  laundry: { title: "Laundry", action: "Request", illustration: "laundry" },
  offers: { title: "Specials", action: "View", illustration: "offers" },
  events: { title: "Events", action: "See", illustration: "events" },
  help: { title: "Help", action: "Call", illustration: "help" },
  stay: { title: "Facilities", action: "Explore", illustration: "stay" },
};

const FILTER_RESULT_GROUPS = {
  eat: ["Food", "Services"],
  room: ["Services"],
  stay: ["Events", "Info", "Services"],
  help: ["Services", "Info"],
};

const GuestHub = () => {
  const navigate = useNavigate();
  const session = useGuestSession();
  const { searchOpen, closeSearch } = useGuestHubSearch();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [ratingOpen, setRatingOpen] = useState(false);

  const go = (path) => navigate(withGuestSearch(path, session.guestSearch));

  useEffect(() => {
    if (searchOpen) return undefined;
    setQuery("");
    setFilter("all");
    return undefined;
  }, [searchOpen]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const results = [];

    session.visibleServices.forEach((service) => {
      if (filter !== "all" && service.category !== filter) return;
      const hay = [service.title, service.hint, ...(service.synonyms || [])]
        .join(" ")
        .toLowerCase();
      if (hay.includes(q)) {
        results.push({
          id: service.id,
          group: "Services",
          title: service.title,
          subtitle: service.hint,
          path: service.path,
        });
      }
    });

    if (filter === "all" || filter === "stay") {
      (session.portalConfig.events || []).forEach((event) => {
        const hay = `${event.title} ${event.when} ${event.where} ${event.hint} event`.toLowerCase();
        if (hay.includes(q)) {
          results.push({
            id: event.id,
            group: "Events",
            title: event.title,
            subtitle: event.when,
            path: "/guest/events",
          });
        }
      });
    }

    if (filter === "all" || filter === "eat") {
      (session.portalConfig.buffet?.meals || []).forEach((meal) => {
        const hay = `${meal.title} ${meal.hours} buffet breakfast lunch dinner`.toLowerCase();
        if (hay.includes(q)) {
          results.push({
            id: meal.id,
            group: "Info",
            title: meal.title,
            subtitle: meal.hours,
            path: "/guest/buffet",
          });
        }
      });
    }

    if (filter === "all" || filter === "stay") {
      const stay = session.portalConfig.stay || {};
      [
        { id: "wifi", title: "Wi-Fi password", subtitle: stay.wifiName, path: "/guest/stay" },
        { id: "parking", title: "Parking", subtitle: stay.parking, path: "/guest/stay" },
      ].forEach((item) => {
        if (`${item.title} ${item.subtitle} wifi password`.toLowerCase().includes(q)) {
          results.push({ ...item, group: "Info" });
        }
      });
    }

    if (filter === "all" || filter === "eat") {
      session.menuItems.forEach((item) => {
        const hay = `${item.name} ${item.description} ${item.categoryName}`.toLowerCase();
        if (hay.includes(q)) {
          results.push({
            id: item.id,
            group: "Food",
            title: item.name,
            subtitle: item.categoryName,
            path: session.isRoom ? "/guest/room-service" : "/guest/dine",
          });
        }
      });
    }

    return results
      .filter((result) => {
        if (filter === "all") return true;
        const groups = FILTER_RESULT_GROUPS[filter] || [];
        return groups.includes(result.group);
      })
      .slice(0, 12);
  }, [query, filter, session]);

  const featured = session.visibleServices.find((service) => service.id === "dine")
    || session.visibleServices.find((service) => service.id === "room-service")
    || session.visibleServices[0];

  const quickIds = session.isRoom ? ROOM_QUICK_IDS : TABLE_QUICK_IDS;
  const forYouIds = session.isRoom ? ROOM_FOR_YOU_IDS : TABLE_FOR_YOU_IDS;
  const hubEvents = (session.portalConfig.events || []).filter((event) => event?.image).slice(0, 3);

  const handleQuick = (id) => {
    if (id === "makeup") {
      const next = !session.makeupRoom;
      session.setMakeupRoom(next);
      toast.success(next ? "Make up room requested" : "Make up room cancelled");
      return;
    }
    const service = session.visibleServices.find((service) => service.id === id);
    if (service) go(service.path);
  };

  const exploreServices = [
    ...forYouIds
      .map((id) => session.visibleServices.find((service) => service.id === id))
      .filter(Boolean),
    ...session.visibleServices.filter((service) => {
      if (forYouIds.includes(service.id)) return false;
      if (service.id === "requests") return false;
      return true;
    }),
    ...(session.portalConfig.modules.rate !== false
      ? [{ id: "rate", title: "Rate your stay", hint: "Takes 20 seconds", category: "stay" }]
      : []),
  ]
    .filter((service) => filter === "all" || service.category === filter)
    .slice(0, 6);

  const quickActions = quickIds.filter((id) => {
    if (id === "makeup") return true;
    const service = session.visibleServices.find((item) => item.id === id);
    if (!service) return false;
    if (filter !== "all" && service.category !== filter) return false;
    return true;
  });

  const showEvents = filter === "all" || filter === "stay";
  const showFeatured = filter === "all" || filter === "eat";

  if (session.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: LUXURY.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 0.4, pb: 2.5, maxWidth: LUXURY.maxWidth, mx: "auto" }}>
      <HubSearch
        open={searchOpen}
        onClose={closeSearch}
        value={query}
        onChange={setQuery}
        filter={filter}
        onFilterChange={setFilter}
        results={searchResults}
        onSelect={(result) => {
          closeSearch();
          go(result.path);
        }}
      />

      {showFeatured && featured ? (
        <Box sx={{ mb: 1.7 }}>
          <FeaturedFoodCard
            title="Extraordinary Flavors Await"
            hint={featured.hint || "Discover chef specialties and seasonal dishes from your room."}
            onClick={() => go(featured.path)}
            dots={Math.max(2, Math.min(4, (session.portalConfig.events || []).length + 1))}
          />
        </Box>
      ) : null}

      {showEvents && hubEvents.length > 0 ? (
        <Box sx={{ mt: showFeatured ? 0.2 : 0 }}>
          <EventsCarousel
            events={hubEvents}
            onSelect={() => go("/guest/events")}
            onViewAll={() => go("/guest/events")}
          />
        </Box>
      ) : null}

      {quickActions.length > 0 ? (
        <Box sx={{ mt: 2.4 }}>
          <SectionHeader
            title="Quick Actions"
            onAction={() => go(session.isRoom ? "/guest/stay" : "/guest/help")}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(quickActions.length, 5)}, minmax(0, 1fr))`,
              gap: 0.75,
            }}
          >
            {quickActions.map((id) => {
              const meta = QUICK_META[id];
              if (!meta) return null;
              return (
                <QuickAction
                  key={id}
                  title={meta.title}
                  actionLabel={id === "makeup" && session.makeupRoom ? "Requested" : meta.action}
                  illustration={meta.illustration}
                  onClick={() => handleQuick(id)}
                />
              );
            })}
          </Box>
        </Box>
      ) : null}

      {exploreServices.length > 0 ? (
        <Box sx={{ mt: 2.5 }}>
          <SectionHeader
            title="Explore More"
            onAction={() => go("/guest/stay")}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0.85,
            }}
          >
            {exploreServices.map((service, index) => (
              <Box
                key={service.id}
                component={motion.div}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                sx={{ height: "100%", display: "flex" }}
              >
                <ServiceTile
                  title={service.title}
                  hint={service.hint}
                  illustration={service.id}
                  onClick={() =>
                    service.id === "rate" ? setRatingOpen(true) : go(service.path)
                  }
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box sx={{ mt: 2.5 }}>
        <ConciergeBanner
          onClick={() =>
            go(
              session.visibleServices.some((s) => s.id === "concierge")
                ? "/guest/concierge"
                : "/guest/help"
            )
          }
        />
      </Box>

      <RatingDialog
        open={ratingOpen}
        onClose={() => setRatingOpen(false)}
        companyId={session.companyId}
        restaurantName={session.company?.name}
      />
    </Box>
  );
};

export default GuestHub;
